import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
    makeStyles,
    Tooltip,
    Popover,
    PopoverTrigger,
    PopoverSurface,
    Input,
    Label,
    Dropdown,
    Option,
    useId,
    TabList,
    Tab,
    SelectTabData,
    tokens,
    Checkbox
} from "@fluentui/react-components";
import {
    Dismiss24Regular,
    Call20Regular,
    Phone20Regular,
    Mail20Regular,
    Info20Regular,
    Add20Regular,
    CheckmarkCircle20Filled,
    Circle20Regular,
    Edit20Regular,
    Delete20Regular,
    ChevronDown12Regular,
    ClockBill20Regular,
} from "@fluentui/react-icons";
import { SocialIcon } from 'react-social-icons';
import { ContactDetailsFormValues } from "../types/Contact";
import { apiUrl } from "../utils/api";
import { useTelephony } from "../context/TelephonyContext";
// Outbound = app-weiter callNumber()-Helper → In-App-SDK-Widget (ADR 0002).

const useStyles = makeStyles({
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        minHeight: "400px" // Ensure enough space
    },
    sectionTitle: {
        fontWeight: "bold",
        fontSize: "14px",
    },
    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "16px",
        marginBottom: "8px"
    },
    actionCell: {
        width: "80px",
        textAlign: "right",
        display: "flex",
        justifyContent: "flex-end",
        gap: "4px"
    },
    standardCell: {
        width: "80px",
        textAlign: "center"
    },
    formContainer: {
        marginTop: "16px",
        padding: "16px",
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        gap: "12px"
    },
    formHeader: {
        fontWeight: "bold",
        marginBottom: "8px",
        display: 'flex',
        justifyContent: 'space-between'
    },
    formRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
    },
    fullWidth: {
        gridColumn: "1 / -1",
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    tabs: {
        marginBottom: "10px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`
    },
    tableContainer: {
        overflowX: "auto",
        width: "100%"
    }
});

interface ContactModesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contact: ContactDetailsFormValues | null;
    mode: "phone" | "email";
}



const NameCell: React.FC<{ name?: string }> = ({ name }) => {
    if (!name) return null;
    const maxLength = 25;
    if (name.length <= maxLength) {
        return <>{name}</>;
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>{name.substring(0, maxLength)}...</span>
            <Popover>
                <PopoverTrigger disableButtonEnhancement>
                    <Button appearance="transparent" icon={<Info20Regular />} size="small" />
                </PopoverTrigger>
                <PopoverSurface>
                    <div style={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>{name}</div>
                </PopoverSurface>
            </Popover>
        </div>
    );
};

export const ContactModesDialog: React.FC<ContactModesDialogProps> = ({
    open,
    onOpenChange,
    contact,
    mode: initialMode,
}) => {
    const styles = useStyles();
    const dropdownId = useId("type-dropdown");
    const { callNumber } = useTelephony();
    // Outbound dialing geht app-weit über callNumber() ans In-App-SDK-Widget.

    // Derive contact name
    const contactName = contact?.displayName ||
        `${contact?.vorname || ''} ${contact?.lastname || ''}`.trim() ||
        (contact as any)?.company_name || // Fallback for company
        'Unknown';


    const contactInfo = {
        id: contact?.kontaktId,
        name: contactName,
        photo_url: contact?.photo_url,
    };

    // Tab State
    const [selectedTab, setSelectedTab] = useState<"phone" | "email">("phone");

    // Internal state for optimistic updates
    const [phoneItems, setPhoneItems] = useState<any[]>([]);
    const [emailItems, setEmailItems] = useState<any[]>([]);

    // Edit/Add State
    const [editingItem, setEditingItem] = useState<any | null>(null); // If set, we are editing
    const [isAdding, setIsAdding] = useState(false); // If true, we are adding new

    // Form Fields
    const [formType, setFormType] = useState<string>('phone');
    const [formLabel, setFormLabel] = useState("");
    const [formValue, setFormValue] = useState("");
    const [formNote, setFormNote] = useState("");
    const [formIsDefault, setFormIsDefault] = useState(false);

    // Reset/Init when opening
    useEffect(() => {
        if (open && contact) {
            setPhoneItems(contact.phones || []);
            setEmailItems(contact.emails || []);
            setSelectedTab(initialMode);
            resetForm();
        } else if (!open) {
            resetForm();
        }
    }, [contact, initialMode, open]);

    // Update formType default when tab changes
    useEffect(() => {
        setFormType(selectedTab === 'phone' ? 'phone' : 'email');
        resetForm();
    }, [selectedTab]);

    const resetForm = () => {
        setEditingItem(null);
        setIsAdding(false);
        setFormLabel("");
        setFormValue("");
        setFormNote("");
        setFormIsDefault(false);
        setFormType(selectedTab === 'phone' ? 'phone' : 'email');
    };

    if (!contact) return null;

    const isPhoneMode = selectedTab === "phone";
    const currentItems = isPhoneMode ? phoneItems : emailItems;
    const setCurrentItems = isPhoneMode ? setPhoneItems : setEmailItems;

    const relatedItems = contact.relatedCommunications?.filter((rc) => {
        if (isPhoneMode) {
            return (
                rc.type === "phone" || rc.type === "mobile" || rc.type === "fax"
            );
        } else {
            return rc.type === "email";
        }
    });

    const getIconForType = (type?: string) => {
        if (type === 'mobile') return <Phone20Regular />;
        if (type === 'phone' || type === 'fax') return <Call20Regular />;
        if (!isPhoneMode) return <Mail20Regular />;
        return <Call20Regular />;
    };


    const handleSave = async () => {
        if (!formValue) return;

        const payload = {
            contact: { id: contact.kontaktId },
            type: formType,
            label: formLabel,
            value: formValue,
            isDefault: formIsDefault,
            note: formNote // Assuming backend might support it or ignores it
        };

        try {
            if (isAdding) {
                const res = await fetch(apiUrl("contact-communication"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                if (res.ok) {
                    const created = await res.json();
                    let newItems = [...currentItems, { ...created, note: formNote }];
                    if (formIsDefault) {
                        newItems = newItems.map(i => ({ ...i, isDefault: i.id === created.id }));
                    }
                    setCurrentItems(newItems);
                    resetForm();
                }
            } else if (editingItem) {
                const res = await fetch(apiUrl(`contact-communication/${editingItem.id}`), {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                if (res.ok) {
                    const updated = await res.json();
                    let newItems = currentItems.map(i => i.id === editingItem.id ? { ...updated, note: formNote } : i);
                    if (formIsDefault) {
                        newItems = newItems.map(i => ({ ...i, isDefault: i.id === editingItem.id }));
                    }
                    setCurrentItems(newItems);
                    resetForm();
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    const onTabSelect = (event: SelectTabData) => {
        setSelectedTab(event.value as "phone" | "email");
    };

    return (
        <Dialog open={open} onOpenChange={(event, data) => onOpenChange(data.open)}>
            <DialogSurface aria-label="Kommunikation" style={{ width: "1100px", maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
                <DialogBody>
                    <DialogTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="close"
                                icon={<Dismiss24Regular />}
                                onClick={() => onOpenChange(false)}
                            />
                        }
                    >
                        {selectedTab === "phone" ? "Telefonnummern" : "Email-Adressen"} - {contact.displayName}
                    </DialogTitle>
                    <DialogContent className={styles.content}>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, marginBottom: "10px" }}>
                            <TabList selectedValue={selectedTab} onTabSelect={(_, data) => onTabSelect(data)}>
                                <Tab value="phone">Telefon</Tab>
                                <Tab value="email">Email</Tab>
                            </TabList>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                {contact.socialMedia?.map((sm) => (
                                    <div
                                        key={sm.id}
                                        onClick={() => sm.value && window.open(sm.value, '_blank')}
                                        style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                                    >
                                        <SocialIcon
                                            network={sm.type === 'homepage' ? undefined : sm.type}
                                            url={sm.type === 'homepage' ? sm.value : undefined}
                                            style={{ height: 20, width: 20 }}
                                            borderRadius="4px"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Direct Communications */}
                        <div className={styles.headerRow}>
                            <div className={styles.sectionTitle}>
                                {contact.typ === "Firma" ? "Zentrale / Allgemein" : "Kontaktdaten"}
                                {contact?.erreichbarkeit && (
                                    <Tooltip content={contact.erreichbarkeit} relationship="label">
                                        <Button
                                            appearance="transparent"
                                            icon={<ClockBill20Regular style={{ fontSize: "16px", color: tokens.colorNeutralForeground3 }} />}
                                            style={{ marginLeft: '8px', cursor: 'help', minWidth: 'auto', padding: 0 }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </div>

                        <div className={styles.tableContainer}>
                            <Table aria-label="Direct Communications">
                                <TableHeader>
                                    <TableRow>
                                        {isPhoneMode && <TableHeaderCell style={{ width: '40px' }}>Typ</TableHeaderCell>}
                                        <TableHeaderCell>Bezeichnung</TableHeaderCell>
                                        <TableHeaderCell>{isPhoneMode ? "Nummer" : "Adresse"}</TableHeaderCell>
                                        <TableHeaderCell className={styles.actionCell}>Aktion</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {currentItems && currentItems.length > 0 ? (
                                        currentItems.map((item, idx) => (
                                            <TableRow key={item.id ?? idx}>
                                                {isPhoneMode && <TableCell>{getIconForType(item.type)}</TableCell>}
                                                <TableCell>{item.label || (item.type === 'phone' ? 'Privat' : (item.type === 'mobile' ? 'Mobil' : item.type))}</TableCell>
                                                <TableCell>{isPhoneMode ? (item as any).number || (item as any).value : (item as any).address || (item as any).value}</TableCell>
                                                <TableCell className={styles.actionCell}>
                                                    {isPhoneMode ? (
                                                        <Button
                                                            appearance="transparent"
                                                            icon={<Call20Regular />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                callNumber((item as any).number || (item as any).value);
                                                            }}
                                                            aria-label="Anrufen"
                                                        />
                                                    ) : (
                                                        <Button
                                                            appearance="transparent"
                                                            icon={<Mail20Regular />}
                                                            onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${(item as any).address || (item as any).value}`; }}
                                                            aria-label="E-Mail senden"
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={isPhoneMode ? 6 : 5}>
                                                Keine {isPhoneMode ? "Telefonnummern" : "Email-Adressen"} vorhanden.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Related Communications (Ansprechpartner) */}
                        {(contact.typ === "Firma" || (relatedItems && relatedItems.length > 0)) && (
                            <>
                                <div className={styles.sectionTitle} style={{ marginTop: '24px' }}>Rolle im Unternemen und Ansprechpartner</div>
                                <div className={styles.tableContainer}>
                                    <Table aria-label="Related Communications">
                                        <TableHeader>
                                            <TableRow>
                                                {isPhoneMode && <TableHeaderCell style={{ width: '40px' }}>Typ</TableHeaderCell>}
                                                <TableHeaderCell>Bezeichnung</TableHeaderCell>
                                                <TableHeaderCell>Name</TableHeaderCell>
                                                <TableHeaderCell>{isPhoneMode ? "Nummer" : "Adresse"}</TableHeaderCell>
                                                <TableHeaderCell>Rolle</TableHeaderCell>
                                                <TableHeaderCell>Fuktion</TableHeaderCell>
                                                <TableHeaderCell>Erreichbarkeit</TableHeaderCell>

                                                <TableHeaderCell className={styles.actionCell}>Aktion</TableHeaderCell>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {relatedItems && relatedItems.length > 0 ? (
                                                relatedItems.map((item, idx) => (
                                                    <TableRow key={item.id ?? idx}>
                                                        {isPhoneMode && <TableCell>{getIconForType(item.type)}</TableCell>}
                                                        <TableCell>{item.label || (item.type === 'phone' ? 'Privat' : (item.type === 'mobile' ? 'Mobil' : item.type))}</TableCell>
                                                        <TableCell><NameCell name={item.name} /></TableCell>
                                                        <TableCell>{item.value}</TableCell>
                                                        <TableCell>{item.role}</TableCell>
                                                        <TableCell></TableCell>
                                                        <TableCell>
                                                            {item.erreichbarkeit && (
                                                                <Tooltip content={item.erreichbarkeit} relationship="label">
                                                                    <Button
                                                                        appearance="transparent"
                                                                        icon={<ClockBill20Regular style={{ fontSize: "16px", color: tokens.colorNeutralForeground3 }} />}
                                                                        style={{ cursor: 'help', minWidth: 'auto', padding: 0 }}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>

                                                        <TableCell className={styles.actionCell}>
                                                            {isPhoneMode ? (
                                                                <Button
                                                                    appearance="transparent"
                                                                    icon={<Call20Regular />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        callNumber(item.value);
                                                                    }}
                                                                    aria-label="Anrufen"
                                                                />
                                                            ) : (
                                                                <Button
                                                                    appearance="transparent"
                                                                    icon={<Mail20Regular />}
                                                                    onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${item.value}`; }}
                                                                    aria-label="E-Mail senden"
                                                                />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={isPhoneMode ? 7 : 6}>
                                                        Keine {isPhoneMode ? "Ansprechpartner mit Telefonnummern" : "Ansprechpartner mit Email-Adressen"} vorhanden.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions style={{ marginTop: 20 }}>
                        <Button appearance="secondary" onClick={() => onOpenChange(false)}>
                            Schließen
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
